/**
 * Mobile Completion Error Boundary
 * Handles errors specific to mobile completion flow (ReturnToDesktopScreen)
 * Provides graceful error recovery for analytics, reporting, and window operations
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { VerificationScreenTemplate } from './VerificationScreenTemplate';
import { logger } from '../../utils/logger';
import { LanguageContext, tUnsafe } from '../../i18n';

const mobileErrorLogger = logger.clone({ labels: { component: 'MobileCompletionErrorBoundary' }});

interface MobileCompletionErrorBoundaryProps {
  children: ReactNode;
}

interface MobileCompletionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class MobileCompletionErrorBoundary extends Component<
  MobileCompletionErrorBoundaryProps,
  MobileCompletionErrorBoundaryState
> {
  static contextType = LanguageContext;

  constructor(props: MobileCompletionErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): MobileCompletionErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo): void {
    mobileErrorLogger.error('Mobile completion error caught by boundary', error instanceof Error ? error : new Error(`${error}`));
  }

  private handleCloseWindow = (): void => {
    try {
      window.close();
    } catch (error) {
      mobileErrorLogger.warn('Could not close window programmatically', error instanceof Error ? error : new Error(`${error}`));
      alert(tUnsafe('mobileCompletion.closeWindowFallback'));
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <VerificationScreenTemplate
          title={tUnsafe('mobileCompletion.title')}
          description={tUnsafe('mobileCompletion.description')}
          icon={{
            src: '/icons/computer_icon.svg',
            alt: tUnsafe('mobileCompletion.iconAlt'),
            size: '3xl',
          }}
          primaryAction={{
            label: tUnsafe('common.closeWindow'),
            onClick: this.handleCloseWindow,
            disabled: false,
          }}
          desktopOptimized={false}
        />
      );
    }

    return this.props.children;
  }
}
